import React, { useCallback, useMemo, useState } from 'react';
import api from '../services/api';

const AGENT_API_BASE_URL = process.env.REACT_APP_AGENT_API_URL || '';
const ANALYTICS_API_URL = AGENT_API_BASE_URL ? `${AGENT_API_BASE_URL}/api` : '/api';
const ANALYSIS_TYPE_NAME_PATTERN = /^[a-z0-9][a-z0-9-_]*$/;

const buildLocalRowId = () => (
  `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
);

const normalizeTypeKind = (value) => {
  const normalizedValue = String(value || '').trim().toLowerCase();
  return normalizedValue === 'extra' ? 'extra' : 'main';
};

const createPersistedRow = (item) => {
  const name = String(item?.name || '').trim().toLowerCase();
  const label = String(item?.label || '').trim() || name;
  const type = normalizeTypeKind(item?.type);
  const persistedId = String(item?.id || '').trim();

  return {
    rowId: persistedId ? `persisted-${persistedId}` : buildLocalRowId(),
    persistedId,
    isPersisted: !!persistedId,
    isProtected: !!item?.isProtected || !!item?.settings?.protected,
    name,
    label,
    type,
    original: {
      name,
      label,
      type
    }
  };
};

const createNewRow = () => ({
  rowId: buildLocalRowId(),
  persistedId: '',
  isPersisted: false,
  isProtected: false,
  name: '',
  label: '',
  type: 'main',
  original: {
    name: '',
    label: '',
    type: 'main'
  }
});

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message || error?.message || fallback
);

const getInUseDetails = (usage = []) => (
  usage
    .map((item) => {
      const usageKinds = [];
      if (item?.usesAsMain) usageKinds.push('main');
      if (item?.usesAsExtra) usageKinds.push('extra');
      const suffix = usageKinds.length ? ` (${usageKinds.join(', ')})` : '';
      return `${item?.scriptName || 'Untitled script'}${suffix}`;
    })
    .filter(Boolean)
);

const AnalyticsSetupPageAnalysisTypes = ({ onTypesChanged }) => {
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [rowSaveState, setRowSaveState] = useState({});
  const [rowDeleteState, setRowDeleteState] = useState({});
  const [rowError, setRowError] = useState({});
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    rowId: '',
    name: ''
  });
  const [noticeModal, setNoticeModal] = useState({
    open: false,
    title: '',
    message: '',
    details: []
  });

  const loadTypes = useCallback(async () => {
    setLoadingTypes(true);
    setLoadError('');

    try {
      const { data } = await api.get('/analysis-type-settings/manage', { baseURL: ANALYTICS_API_URL });
      if (!data?.success) {
        throw new Error(data?.message || 'Failed to load analysis types');
      }

      const normalizedRows = Array.isArray(data?.types)
        ? data.types
          .map((item) => createPersistedRow(item))
          .filter((item) => item?.name && item?.type)
        : [];

      setTypes(normalizedRows);
    } catch (error) {
      setTypes([]);
      setLoadError(getErrorMessage(error, 'Failed to load analysis types'));
    } finally {
      setLoadingTypes(false);
    }
  }, []);

  const sortedTypes = useMemo(() => {
    const sortWithinGroup = (rows) => {
      const persisted = rows.filter((row) => row.isPersisted);
      const local = rows.filter((row) => !row.isPersisted);

      return [...persisted, ...local];
    };

    const protectedRows = types.filter((row) => !!row.isProtected);
    const nonProtectedRows = types.filter((row) => !row.isProtected);

    return [...sortWithinGroup(protectedRows), ...sortWithinGroup(nonProtectedRows)];
  }, [types]);

  React.useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const updateRow = useCallback((rowId, patch) => {
    setTypes((prev) => prev.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)));
  }, []);

  const removeRow = useCallback((rowId) => {
    setTypes((prev) => prev.filter((row) => row.rowId !== rowId));
    setRowError((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setRowSaveState((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setRowDeleteState((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  }, []);

  const handleAddType = () => {
    setTypes((prev) => [...prev, createNewRow()]);
    setLoadError('');
  };

  const isRowDirty = useCallback((row) => (
    row.name !== row.original.name
    || row.label !== row.original.label
    || row.type !== row.original.type
  ), []);

  const validateRow = useCallback((row) => {
    if (row.isPersisted) return '';

    const normalizedName = String(row.name || '').trim().toLowerCase();
    if (!normalizedName) {
      return 'Name is required.';
    }
    if (normalizedName === 'none') {
      return 'Name "none" is reserved.';
    }
    if (!ANALYSIS_TYPE_NAME_PATTERN.test(normalizedName)) {
      return 'Name can include lowercase letters, numbers, dashes, and underscores only.';
    }
    return '';
  }, []);

  const handleSaveType = async (row) => {
    const validationError = validateRow(row);
    if (validationError) {
      setRowError((prev) => ({ ...prev, [row.rowId]: validationError }));
      return;
    }

    setRowSaveState((prev) => ({ ...prev, [row.rowId]: 'saving' }));
    setRowError((prev) => ({ ...prev, [row.rowId]: '' }));

    try {
      const payload = {
        id: row.isPersisted ? row.persistedId : undefined,
        name: row.isPersisted ? undefined : String(row.name || '').trim().toLowerCase(),
        label: row.label,
        type: row.isProtected ? row.original.type : normalizeTypeKind(row.type)
      };
      const { data } = await api.post('/analysis-type-settings/manage/save', payload, { baseURL: ANALYTICS_API_URL });

      if (!data?.success) {
        throw new Error(data?.message || 'Failed to save analysis type');
      }

      const savedRow = createPersistedRow(data?.analysisType || {});
      setTypes((prev) => prev.map((item) => (item.rowId === row.rowId ? savedRow : item)));
      setRowSaveState((prev) => {
        const next = { ...prev };
        delete next[row.rowId];
        next[savedRow.rowId] = 'saved';
        return next;
      });
      window.setTimeout(() => {
        setRowSaveState((prev) => ({ ...prev, [savedRow.rowId]: 'idle' }));
      }, 1800);

      await onTypesChanged?.();
    } catch (error) {
      setRowSaveState((prev) => ({ ...prev, [row.rowId]: 'idle' }));
      setRowError((prev) => ({ ...prev, [row.rowId]: getErrorMessage(error, 'Failed to save analysis type') }));
    }
  };

  const handleRequestDelete = (row) => {
    setDeleteModal({
      open: true,
      rowId: row.rowId,
      name: row.name || 'Untitled'
    });
  };

  const closeDeleteModal = () => {
    const deleteState = rowDeleteState[deleteModal.rowId];
    if (deleteState === 'deleting') return;
    setDeleteModal({ open: false, rowId: '', name: '' });
  };

  const handleConfirmDelete = async () => {
    const targetRow = types.find((row) => row.rowId === deleteModal.rowId);
    if (!targetRow) {
      closeDeleteModal();
      return;
    }

    if (!targetRow.isPersisted) {
      removeRow(targetRow.rowId);
      setDeleteModal({ open: false, rowId: '', name: '' });
      return;
    }

    setRowDeleteState((prev) => ({ ...prev, [targetRow.rowId]: 'deleting' }));
    setRowError((prev) => ({ ...prev, [targetRow.rowId]: '' }));

    try {
      const { data } = await api.post('/analysis-type-settings/manage/delete', {
        id: targetRow.persistedId
      }, { baseURL: ANALYTICS_API_URL });

      if (!data?.success) {
        throw new Error(data?.message || 'Failed to delete analysis type');
      }

      removeRow(targetRow.rowId);
      setDeleteModal({ open: false, rowId: '', name: '' });
      await onTypesChanged?.();
    } catch (error) {
      setRowDeleteState((prev) => ({ ...prev, [targetRow.rowId]: 'idle' }));

      const responseData = error?.response?.data || {};
      if (responseData?.code === 'ANALYSIS_TYPE_IN_USE') {
        setDeleteModal({ open: false, rowId: '', name: '' });
        setNoticeModal({
          open: true,
          title: 'Type Is In Use',
          message: responseData?.message || 'This analysis type cannot be deleted because scripts currently use it.',
          details: getInUseDetails(responseData?.usage || [])
        });
        return;
      }

      if (responseData?.code === 'ANALYSIS_TYPE_PROTECTED') {
        setDeleteModal({ open: false, rowId: '', name: '' });
        setNoticeModal({
          open: true,
          title: 'Deletion Not Allowed',
          message: responseData?.message || 'This analysis type is protected and cannot be deleted.',
          details: []
        });
        return;
      }

      setRowError((prev) => ({
        ...prev,
        [targetRow.rowId]: getErrorMessage(error, 'Failed to delete analysis type')
      }));
    }
  };

  return (
    <>
      <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <h2 className="text-base font-semibold text-gray-900">Analysis types</h2>
            <p className="text-xs text-gray-500">
              Name is immutable after creation. Protected system types cannot be deleted or retyped.
            </p>
          </div>
          <button
            type="button"
            onClick={handleAddType}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600 text-lg font-semibold text-white transition hover:bg-emerald-700"
            title="Add analysis type"
          >
            +
          </button>
        </div>

        {loadingTypes && (
          <div className="text-xs text-gray-400">Loading analysis types...</div>
        )}

        {loadError && (
          <div className="text-sm text-red-600">{loadError}</div>
        )}

        {!loadingTypes && !loadError && sortedTypes.length === 0 && (
          <div className="text-sm text-gray-500">
            No analysis types found. Click + to create one.
          </div>
        )}

        {!loadError && sortedTypes.length > 0 && (
          <div className="space-y-2">
            {sortedTypes.map((row) => {
              const saveState = rowSaveState[row.rowId] || 'idle';
              const deleteState = rowDeleteState[row.rowId] || 'idle';
              const dirty = isRowDirty(row);
              const validationError = validateRow(row);
              const protectedType = !!row.isProtected;
              const disableTypeSelect = saveState === 'saving' || deleteState === 'deleting' || protectedType;
              const disableSave = saveState === 'saving' || deleteState === 'deleting' || !dirty || !!validationError;
              const disableDelete = deleteState === 'deleting' || protectedType;

              return (
                <div key={row.rowId} className="space-y-2 rounded-lg border border-gray-200 bg-gray-50/40 p-3">
                  <div className="grid gap-2 md:grid-cols-[minmax(160px,1fr)_minmax(180px,1fr)_140px_auto_auto] md:items-center">
                    {row.isPersisted ? (
                      <input
                        type="text"
                        value={row.name}
                        readOnly
                        className="w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-700"
                      />
                    ) : (
                      <input
                        type="text"
                        value={row.name}
                        onChange={(event) => updateRow(row.rowId, { name: event.target.value.toLowerCase() })}
                        placeholder="name (ex: qa-check)"
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    )}

                    <input
                      type="text"
                      value={row.label}
                      onChange={(event) => updateRow(row.rowId, { label: event.target.value })}
                      placeholder="Label"
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />

                    <select
                      value={row.type}
                      onChange={(event) => updateRow(row.rowId, { type: normalizeTypeKind(event.target.value) })}
                      disabled={disableTypeSelect}
                      title={protectedType ? 'Protected analysis types cannot change kind.' : ''}
                      className={`rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        disableTypeSelect
                          ? 'border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed'
                          : 'border-gray-300 bg-white text-gray-900'
                      }`}
                    >
                      <option value="main">main</option>
                      <option value="extra">extra</option>
                    </select>

                    <button
                      type="button"
                      onClick={() => handleSaveType(row)}
                      disabled={disableSave}
                      className={`inline-flex w-fit items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition ${
                        disableSave
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {saveState === 'saving' ? 'Saving...' : 'Save'}
                    </button>

                    <div className="relative group w-fit">
                      <button
                        type="button"
                        onClick={() => handleRequestDelete(row)}
                        disabled={disableDelete}
                        className={`inline-flex h-9 w-9 items-center justify-center rounded-lg text-lg font-semibold transition ${
                          disableDelete
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-rose-600 text-white hover:bg-rose-700'
                        }`}
                        title={!protectedType ? 'Delete analysis type' : ''}
                      >
                        -
                      </button>

                      {protectedType && (
                        <div className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-max -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                          This type is protected and cannot be deleted.
                        </div>
                      )}
                    </div>
                  </div>

                  {validationError && (
                    <div className="text-xs text-amber-600">{validationError}</div>
                  )}

                  {!!rowError[row.rowId] && (
                    <div className="text-xs text-red-600">{rowError[row.rowId]}</div>
                  )}

                  {saveState === 'saved' && !rowError[row.rowId] && (
                    <div className="text-xs text-emerald-600">Saved successfully.</div>
                  )}

                </div>
              );
            })}
          </div>
        )}
      </section>

      {deleteModal.open && (
        <div className="fixed inset-0 z-[82] flex items-center justify-center bg-slate-900/55 px-4">
          <div className="w-full max-w-lg rounded-xl border border-rose-200 bg-white shadow-2xl">
            <div className="border-b border-rose-100 px-5 py-4">
              <h3 className="text-base font-semibold text-slate-900">Delete Analysis Type</h3>
            </div>
            <div className="space-y-3 px-5 py-4">
              <p className="text-sm text-slate-700">
                Are you sure you want to delete "{deleteModal.name}"?
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                onClick={closeDeleteModal}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {noticeModal.open && (
        <div className="fixed inset-0 z-[83] flex items-center justify-center bg-slate-900/55 px-4">
          <div className="w-full max-w-lg rounded-xl border border-amber-200 bg-white shadow-2xl">
            <div className="border-b border-amber-100 px-5 py-4">
              <h3 className="text-base font-semibold text-slate-900">{noticeModal.title || 'Notice'}</h3>
            </div>
            <div className="space-y-3 px-5 py-4">
              <p className="text-sm text-slate-700">{noticeModal.message}</p>
              {noticeModal.details.length > 0 && (
                <div className="space-y-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                  {noticeModal.details.map((item, index) => (
                    <div key={`${item}-${index}`}>{item}</div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                onClick={() => setNoticeModal({ open: false, title: '', message: '', details: [] })}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AnalyticsSetupPageAnalysisTypes;
